// Converte un PDF testuale in HTML per l'editor lettere, preservando la formattazione principale.
const FAMILIES = [
  [/helvetica/i, "Helvetica, Arial, sans-serif"],
  [/arial/i, "Arial, Helvetica, sans-serif"],
  [/times/i, "'Times New Roman', Times, serif"],
  [/courier/i, "'Courier New', Courier, monospace"],
  [/georgia/i, "Georgia, 'Times New Roman', serif"],
  [/verdana/i, "Verdana, Geneva, sans-serif"],
  [/calibri/i, "Calibri, 'Segoe UI', sans-serif"],
];
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const fontInfo = (page, styles, id) => {
  let f = null;
  try { f = page.commonObjs.has(id) ? page.commonObjs.get(id) : null; } catch { f = null; }
  const name = String(f?.name || "").replace(/^[A-Z]{6}\+/, "");
  const fallback = String(styles[id]?.fontFamily || "");
  const fam = FAMILIES.find(([re]) => re.test(name))?.[1]
    || (/mono/i.test(fallback) ? FAMILIES[3][1] : /serif/i.test(fallback) && !/sans/i.test(fallback) ? FAMILIES[2][1] : FAMILIES[1][1]);
  return {
    family: fam,
    bold: !!(f?.bold || f?.black) || /bold|black|heavy|semibold|demi/i.test(name),
    italic: !!f?.italic || /italic|oblique/i.test(name),
  };
};

const buildLines = (page, tc) => {
  const items = tc.items.filter((it) => it.str !== undefined && it.transform).map((it) => {
    const size = Math.hypot(it.transform[2], it.transform[3]) || 10;
    return { str: it.str, x: it.transform[4], y: it.transform[5], w: it.width || 0, size, ...fontInfo(page, tc.styles || {}, it.fontName) };
  }).filter((it) => it.str.trim() || it.w > 0);
  items.sort((a, b) => b.y - a.y || a.x - b.x);
  const lines = [];
  for (const it of items) {
    const ln = lines.find((l) => Math.abs(l.y - it.y) < Math.max(l.size, it.size) * 0.45);
    if (ln) { ln.items.push(it); ln.size = Math.max(ln.size, it.size); } else lines.push({ y: it.y, size: it.size, items: [it] });
  }
  lines.forEach((l) => {
    l.items.sort((a, b) => a.x - b.x);
    const vis = l.items.filter((i) => i.str.trim());
    l.left = vis.length ? vis[0].x : l.items[0].x;
    l.right = Math.max(...l.items.map((i) => i.x + i.w));
  });
  return lines.filter((l) => l.items.some((i) => i.str.trim())).sort((a, b) => b.y - a.y);
};

const lineHtml = (l) => {
  const runs = [];
  let prevEnd = null;
  for (const it of l.items) {
    let s = it.str;
    if (prevEnd !== null && it.x - prevEnd > it.size * 0.15 && !/\s$/.test(runs.at(-1)?.text || "") && !/^\s/.test(s)) s = " " + s;
    prevEnd = it.x + it.w;
    const px = Math.round(it.size * 96 / 72);
    const last = runs.at(-1);
    if (last && last.bold === it.bold && last.italic === it.italic && last.px === px && last.family === it.family) last.text += s;
    else runs.push({ text: s, bold: it.bold, italic: it.italic, px, family: it.family });
  }
  return runs.map((r) => {
    let h = `<span style="font-size: ${r.px}px; font-family: ${r.family}">${esc(r.text)}</span>`;
    if (r.italic) h = `<em>${h}</em>`;
    if (r.bold) h = `<strong>${h}</strong>`;
    return h;
  }).join("");
};

const alignOf = (l, minL, maxR, W) => {
  const lg = l.left - minL, rg = maxR - l.right, tol = Math.max(4, W * 0.012);
  if (lg > 20 && Math.abs(lg - rg) < Math.max(tol * 2, W * 0.04)) return "center";
  if (rg < tol && lg > 40) return "right";
  return "left";
};

const pageHtml = (lines, W) => {
  if (!lines.length) return [];
  const minL = Math.min(...lines.map((l) => l.left));
  const maxR = Math.max(...lines.map((l) => l.right));
  const tol = Math.max(4, W * 0.012);
  const sizes = lines.map((l) => l.size).sort((a, b) => a - b);
  const base = sizes[Math.floor(sizes.length / 2)];
  const gaps = lines.slice(1).map((l, i) => lines[i].y - l.y).filter((g) => g > 0 && g < base * 2.2).sort((a, b) => a - b);
  const ls = gaps.length ? gaps[Math.floor(gaps.length / 2)] : base * 1.2;

  const paras = [];
  lines.forEach((l, i) => {
    l.align = alignOf(l, minL, maxR, W);
    const prev = lines[i - 1];
    const gap = prev ? prev.y - l.y : 0;
    const cur = paras.at(-1);
    const sameBlock = cur && gap <= ls * 1.35 && cur.align === l.align && (l.align !== "left" || Math.abs(l.left - cur.lines.at(-1).left) < tol * 3 || cur.lines.length === 1);
    if (sameBlock) { cur.lines.push(l); return; }
    const blanks = prev ? Math.min(4, Math.max(0, Math.round(gap / ls) - 1)) : 0;
    paras.push({ align: l.align, lines: [l], blanks });
  });

  const out = [];
  for (const p of paras) {
    for (let k = 0; k < p.blanks; k++) out.push("<p></p>");
    const full = (l) => maxR - l.right < tol * 2;
    const justify = p.align === "left" && p.lines.length > 1 && p.lines.slice(0, -1).every(full);
    const align = justify ? "justify" : p.align;
    const indentPt = align === "left" || align === "justify" ? p.lines[0].left - minL : 0;
    const indent = indentPt > 6 ? "\u00a0".repeat(Math.round(indentPt / (p.lines[0].size * 0.28))) : "";
    const inner = p.lines.map(lineHtml).join(justify ? " " : "<br>");
    const lh = p.lines.length > 1 ? Math.round(((p.lines[0].y - p.lines.at(-1).y) / (p.lines.length - 1) / p.lines[0].size) * 20) / 20 : 0;
    const style = [align !== "left" ? `text-align: ${align}` : "", lh >= 1 && lh <= 3 ? `line-height: ${lh}` : ""].filter(Boolean).join("; ");
    out.push(`<p${style ? ` style="${style}"` : ""}>${indent}${inner}</p>`);
  }
  return out;
};

export async function pdfToHtml(pdfjsLib, file, aiPage) {
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const meta = await pdf.getMetadata().catch(() => null);
  const kw = String(meta?.info?.Keywords || "");
  if (kw.startsWith(PGP_HTML_TAG)) {
    try { return decodeHtmlCopy(kw); } catch { /* copia non leggibile: prosegue */ }
  }
  const IMG_OPS = [pdfjsLib.OPS.paintImageXObject, pdfjsLib.OPS.paintJpegXObject, pdfjsLib.OPS.paintInlineImageXObject].filter(Boolean);
  const pages = [];
  let hasText = false;
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const ops = await page.getOperatorList();
    const tc = await page.getTextContent();
    const chars = tc.items.reduce((n, it) => n + (it.str || "").trim().length, 0);
    const hasImage = ops.fnArray.some((f) => IMG_OPS.includes(f));
    if (aiPage && hasImage && chars < 200) {
      const html = await aiPage(await renderPageJpeg(page), i, pdf.numPages);
      if (html) { hasText = true; pages.push(html); continue; }
    }
    const lines = buildLines(page, tc);
    if (lines.length) hasText = true;
    pages.push(pageHtml(lines, page.getViewport({ scale: 1 }).width).join(""));
  }
  return hasText ? pages.filter(Boolean).join("<p></p>") : "";
}

async function renderPageJpeg(page) {
  const vp = page.getViewport({ scale: 2 });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(vp.width); canvas.height = Math.round(vp.height);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return canvas.toDataURL("image/jpeg", 0.85);
}

export const PGP_HTML_TAG = "PGPHTML:";
export const encodeHtmlCopy = (html) => PGP_HTML_TAG + btoa(unescape(encodeURIComponent(html || "")));
const decodeHtmlCopy = (kw) => decodeURIComponent(escape(atob(kw.slice(PGP_HTML_TAG.length))));
